import React, { useEffect, useRef, useState } from 'react';
import {
  addMonths,
  addYears,
  endOfMonth,
  endOfYear,
  formatLongMonthYear,
  isoWeekNumber,
  isSameDay,
  isSameMonth,
  isSameWeek,
  monthGrid,
  MONTHS_SHORT,
  startOfDay,
  startOfMonth,
  startOfYear,
  localOf,
  WEEKDAYS_SHORT,
} from '../../utils/localDate';
import { isPeriodSelectable, NavigablePeriod, PeriodBounds } from './periodModel';

const YEARS_WITHOUT_FLOOR = 9;

interface PeriodCalendarProps {
  type: NavigablePeriod;
  anchor: Date;
  bounds: PeriodBounds;
  onPick: (instant: Date) => void;
}

const cellClass = (selected: boolean, disabled: boolean, outside = false): string =>
  `dr-cell${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}${
    outside ? ' is-outside' : ''
  }`;

export default function PeriodCalendar({
  type,
  anchor,
  bounds,
  onPick,
}: PeriodCalendarProps): React.ReactElement {
  const { min, now } = bounds;
  const anchorTime = anchor.getTime();
  const [cursorTime, setCursorTime] = useState(() => startOfMonth(anchor).getTime());
  const selectedYearRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setCursorTime(startOfMonth(new Date(anchorTime)).getTime());
  }, [anchorTime]);

  useEffect(() => {
    const item = type === 'year' ? selectedYearRef.current : null;
    const list = item?.parentElement;
    if (!item || !list) return;

    // Scrolls the list itself; scrollIntoView would also scroll the page out from under the popover.
    list.scrollTop = item.offsetTop - (list.clientHeight - item.clientHeight) / 2;
  }, [type]);

  const cursor = new Date(cursorTime);
  const setCursor = (next: Date) => setCursorTime(next.getTime());

  const navByYear = type === 'month';
  const shift = (from: Date, n: number) => (navByYear ? addYears(from, n) : addMonths(from, n));
  const unitStart = navByYear ? startOfYear : startOfMonth;
  const unitEnd = navByYear ? endOfYear : endOfMonth;

  const prevDisabled = !!min && unitEnd(shift(cursor, -1)) < startOfDay(min);
  const nextDisabled = unitStart(shift(cursor, 1)) > unitStart(now);

  const renderHeader = () => (
    <div className="dr-nav">
      <button
        type="button"
        className="dr-nav-btn"
        onClick={() => setCursor(shift(cursor, -1))}
        disabled={prevDisabled}
        aria-label={navByYear ? 'Previous year' : 'Previous month'}
      >
        <i className="fa-solid fa-chevron-left" aria-hidden="true" />
      </button>

      <span className="dr-nav-title">
        {navByYear ? cursor.getFullYear() : formatLongMonthYear(cursor)}
      </span>

      <button
        type="button"
        className="dr-nav-btn"
        onClick={() => setCursor(shift(cursor, 1))}
        disabled={nextDisabled}
        aria-label={navByYear ? 'Next year' : 'Next month'}
      >
        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
      </button>
    </div>
  );

  const renderWeekdays = () => (
    <div className={`dr-weekdays${type === 'week' ? ' dr-weekdays--with-wk' : ''}`}>
      {type === 'week' && <span className="dr-wk dr-wk--head">Wk</span>}
      {WEEKDAYS_SHORT.map((day) => (
        <span key={day} className="dr-weekday">
          {day}
        </span>
      ))}
    </div>
  );

  const renderYears = () => {
    const lastYear = now.getFullYear();
    const firstYear = min ? min.getFullYear() : lastYear - YEARS_WITHOUT_FLOOR;
    const years = Array.from({ length: lastYear - firstYear + 1 }, (_, i) => lastYear - i);

    return (
      <div className="dr-list">
        {years.map((year) => {
          const selected = year === anchor.getFullYear();

          return (
            <button
              key={year}
              type="button"
              ref={selected ? selectedYearRef : undefined}
              className={`dr-list-item${selected ? ' is-selected' : ''}`}
              onClick={() => onPick(localOf(year, 0, 1))}
            >
              {year}
              {selected && <i className="fa-solid fa-check dr-list-check" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    );
  };

  const renderMonths = () => (
    <div className="dr-grid dr-grid--3">
      {MONTHS_SHORT.map((label, index) => {
        const month = localOf(cursor.getFullYear(), index, 1);
        const disabled = !isPeriodSelectable('month', month, bounds);

        return (
          <button
            key={label}
            type="button"
            className={cellClass(isSameMonth(month, anchor), disabled)}
            disabled={disabled}
            onClick={() => onPick(month)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const renderDays = () => (
    <div className="dr-grid dr-grid--days">
      {monthGrid(cursor)
        .flat()
        .map((day) => {
          const disabled = !isPeriodSelectable('day', day, bounds);

          return (
            <button
              key={day.getTime()}
              type="button"
              className={cellClass(isSameDay(day, anchor), disabled, !isSameMonth(day, cursor))}
              disabled={disabled}
              onClick={() => onPick(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
    </div>
  );

  const renderWeeks = () => (
    <div className="dr-weeks">
      {monthGrid(cursor).map((row) => {
        const weekStart = row[0];
        const disabled = !isPeriodSelectable('week', weekStart, bounds);
        const selected = isSameWeek(weekStart, anchor);

        return (
          <button
            key={weekStart.getTime()}
            type="button"
            className={`dr-week-row${selected ? ' is-selected' : ''}${
              disabled ? ' is-disabled' : ''
            }`}
            disabled={disabled}
            onClick={() => onPick(weekStart)}
          >
            <span className="dr-wk">{isoWeekNumber(weekStart)}</span>
            <span className="dr-week-bar" aria-hidden="true" />
            {row.map((day) => (
              <span
                key={day.getTime()}
                className={`dr-day${isSameMonth(day, cursor) ? '' : ' is-outside'}`}
              >
                {day.getDate()}
              </span>
            ))}
          </button>
        );
      })}
    </div>
  );

  const showsMonthGrid = type === 'day' || type === 'week';

  return (
    <div className="dr-calendar">
      {type !== 'year' && renderHeader()}
      {showsMonthGrid && renderWeekdays()}

      {type === 'year' && renderYears()}
      {type === 'month' && renderMonths()}
      {type === 'day' && renderDays()}
      {type === 'week' && renderWeeks()}

      {showsMonthGrid && (
        <div className="dr-footer">
          <button type="button" className="dr-footer-link" onClick={() => onPick(now)}>
            {type === 'day' ? 'Today' : 'This week'}
          </button>
        </div>
      )}
    </div>
  );
}
